import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormArray,
  UntypedFormGroup,
  FormGroup,
  Validators,
  FormBuilder,
} from '@angular/forms';

import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Client } from 'src/providers/client/client';
import { Pros } from 'src/providers/pros/pros';
import { Communication } from 'src/providers/communication/communication';
import { Me } from 'src/providers/me/me';

import { Util } from 'src/shared/util/util';
import validationUtils from 'src/shared/util/validation-utils';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { CreateConciergeActionOnboarding } from '../create-concierge-action-onboarding/create-concierge-action-onboarding';
import { OnboardingComponent } from 'src/shared/components/onboarding/onboarding';

@Component({
    selector: 'tidy-add-pros-onboarding',
    templateUrl: './add-pros.component.html',
    styleUrls: ['./add-pros.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})

export class AddProsOnboardingComponent implements OnInit, OnDestroy {

  submitted: boolean;
  forms: UntypedFormArray;
  serviceCategoryItems: any[] = [];
  whereToAddItems: any[] = [];
  propertiesToAddItems: any[] = [];
  loaded: boolean;
  addresses: any[] = [];
  countryCodes: { [formIndex: number]: string } = {};
  private addressId: number;
  currentAddressName = '';
  currentPros: any[] = [];
  isCombinedFlow = false;
  fromGettingStarted = false;

  // Wizard state
  currentStep: 1 | 2 | 3 = 1;

  // Step 2: Smart Numbers
  proxyProNumber = '';
  proxyGuestNumber = '';
  loadingNumbers = false;
  private smartNumbersPromise: Promise<void> | null = null;
  private smartNumberSubscriptionMissing = false;

  // Step 3: Notify Pros
  allProItems: any[] = [];
  addressProItems: any[] = [];
  selectedPros: any[] = [];
  addressName = '';
  didCopyMessage = false;
  generatedMessage = '';
  messageCopied = false;
  userName = '[YOUR NAME]';
  businessName = '[YOUR BUSINESS]';
  sendingInProgress = false;
  prosSendStatus: { pro: any; status: 'pending' | 'sending' | 'success' | 'error'; error?: string }[] = [];
  howToSendItems = [
    { value: 'sms', viewValue: 'Automatically - send SMS to my pros for me' },
    { value: 'copy', viewValue: "Manually - I'll copy and paste the message" }
  ];
  proScopeItems: any[] = [];
  notifyForm: FormGroup;
  notifyLoaded = false;

  boundAddPros = this.addPros.bind(this);
  boundSkipStep = this.skipStep.bind(this);

  get progressPercent(): number {
    return Math.round((this.currentStep / 3) * 100);
  }

  constructor(
    private fb: UntypedFormBuilder,
    private typedFb: FormBuilder,
    private onboardingProvider: OnboardingProvider,
    private pros: Pros,
    private util: Util,
    private client: Client,
    public windowService: WindowService,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private communication: Communication,
    private me: Me,
    private cdr: ChangeDetectorRef
  ) {
    this.forms = fb.array([]);
    this.notifyForm = typedFb.group({
      messageText: [''],
      howToSend: ['sms'],
      proScope: ['address']
    });
  }

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId ? Number(params.addressId) : null;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.isCombinedFlow = params?.combinedFlow === true;
    this.rightSidePanelService.setDialogPageTitle(this.isCombinedFlow ? 'Add / Notify Pros' : 'Add Your Pros');
    if (this.fromGettingStarted) {
      this.smartNumbersPromise = this.generateSmartNumbers();
    }
    try {
      this.loaded = false;
      const [addresses, serviceCategories] = await Promise.all([
        this.client.getAddresses(),
        this.getServiceCategoryItems(),
      ]);
      this.addresses = addresses;
      if (!this.addressId && this.addresses.length > 0) {
        this.addressId = this.addresses[0].id;
      }
      if (this.addressId) {
        const selectedAddress = this.addresses.find(a => Number(a.id) === this.addressId);
        this.currentAddressName = selectedAddress?.address_name || selectedAddress?.address1 || '';
        try {
          this.currentPros = await this.pros.getAllProsByAddress(this.addressId);
        } catch (_err) {
          this.currentPros = [];
        }
      }
      this.getWhereToAddItems(addresses);
      this.serviceCategoryItems = serviceCategories;
      this.pushEmptyForm();
      this.loaded = true;
      this.cdr.markForCheck();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  ngOnDestroy() {
    this.rightSidePanelService.customBackAction$.next(null);
  }

  private updateBackAction() {
    if (this.currentStep > 1 && this.fromGettingStarted) {
      this.rightSidePanelService.customBackAction$.next(() => {
        this.currentStep = (this.currentStep - 1) as 1 | 2 | 3;
        this.cdr.markForCheck();
        this.updateBackAction();
      });
    } else {
      this.rightSidePanelService.customBackAction$.next(null);
    }
  }

  pushEmptyForm() {
    const newForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, validationUtils.validateEmail]],
      phone: ['', [Validators.minLength(10)]],
      whereToAdd: [this.addressId ? 'select' : 'all'],
      propertiesToAdd: [this.addressId ? [this.addressId] : ''],
      serviceCategories: ['', Validators.required],
    });
    this.forms.push(newForm);
    if (this.addressId) {
      this.selectWhereToAdd(newForm);
    }
  }

  removeForm(index: number) {
    this.forms.removeAt(index);
    delete this.countryCodes[index];
    const updatedCountryCodes: { [formIndex: number]: string } = {};
    Object.keys(this.countryCodes).forEach(key => {
      const formIndex = parseInt(key);
      if (formIndex > index) {
        updatedCountryCodes[formIndex - 1] = this.countryCodes[formIndex];
      } else {
        updatedCountryCodes[formIndex] = this.countryCodes[formIndex];
      }
    });
    this.countryCodes = updatedCountryCodes;
  }

  onCountryCodeChange(countryCode: string, formIndex: number) {
    this.countryCodes[formIndex] = countryCode.replace('+', '');
  }

  goToUploadPros() {
    this.rightSidePanelService.setDialogPageTitle('Upload Pros');
    this.rightSidePanelService.navigateTo('onboarding/createConciergeAction', {}, CreateConciergeActionOnboarding);
  }

  async addPros() {
    this.submitted = true;
    this.updatePhoneAndEmailValidation();
    const formIsValid = this.checkIfFormIsValid();
    if (!formIsValid) {
      return this.util.showWarning('Please add more information about your pros.');
    }
    const hasAnyPro = this.forms.controls.some(form => form.get('name').value);
    if (!hasAnyPro && this.currentPros.length === 0) {
      return this.util.showWarning('Please add a pro or skip this step.');
    }
    try {
      const payload = {
        homekeepers: this.forms.value.filter((value) => value.name).map((value, index) => ({
          name: value.name,
          email: value.email,
          phone: value.phone && this.countryCodes[index] ?
            parsePhoneNumber(this.countryCodes[index] + value.phone) :
            value.phone,
          add_to_address_homekeeper_list: {
            address_ids: this.getAddressIds(value),
            service_type_ids: this.getServiceTypeIds(value),
          },
        })),
      };
      if (payload.homekeepers.length > 0) {
        await this.pros.createManyPros(payload);
      }
      await this.onboardingProvider.markGoalAsCompleted('addPros');
      try {
        await this.client.addMetadata({ key: `onboarding_step.${this.addressId}.pros`, value: 'completed' });
      } catch (_err) {
        // Non-fatal
      }
      // Only the legacy combined Add/Notify flow continues into Smart Numbers (step 2) and
      // Notify Pros (step 3). When launched as the independent "Add Pros" item (Funnel V2
      // assigned step — no combinedFlow), Add Pros writes ONLY `pros=completed` and closes.
      if (this.isCombinedFlow) {
        this.currentStep = 2;
        this.updateBackAction();
        this.cdr.markForCheck();
        await this.loadSmartNumbers();
      } else if (this.fromGettingStarted) {
        if (this.windowService.isDesktopRes) {
          this.rightSidePanelService.closeRightSidePanel();
        } else {
          this.navCtrl.navigateForward('getting-started');
        }
      } else {
        if (!this.windowService.isDesktopRes) {
          this.navCtrl.navigateForward('onboarding');
        } else {
          this.rightSidePanelService.setDialogPageTitle('Getting Started');
          this.rightSidePanelService.navigateTo('onboarding', {}, OnboardingComponent);
        }
        this.onboardingProvider.setShowOnboardingOnPage(false);
        this.onboardingProvider.setShowOnboardingInHeader(true);
      }
      this.submitted = false;
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  checkIfFormIsValid(): boolean {
    this.forms.controls.forEach(form => {
      const nameControl = form.get('name');
      const hasName = nameControl.value;
      const emailControl = form.get('email');
      const phoneControl = form.get('phone');
      const serviceCategoriesControl = form.get('serviceCategories');
      if (hasName) {
        nameControl.setValidators([Validators.required]);
        emailControl.setValidators([Validators.required, validationUtils.validateEmail]);
        phoneControl.setValidators([Validators.minLength(10)]);
        serviceCategoriesControl.setValidators([Validators.required]);
      } else {
        nameControl.clearValidators();
        emailControl.clearValidators();
        phoneControl.clearValidators();
        serviceCategoriesControl.clearValidators();
      }
      nameControl.updateValueAndValidity();
      emailControl.updateValueAndValidity();
      phoneControl.updateValueAndValidity();
      serviceCategoriesControl.updateValueAndValidity();
    });
    return this.forms.controls
      .filter(form => form.get('name').value)
      .every(form => form.valid);
  }

  updatePhoneAndEmailValidation() {
    for (let i = 0; i < this.forms.length; i++) {
      const form = this.forms.at(i);
      const emailControl = form.get('email');
      const phoneControl = form.get('phone');
      if (emailControl.valid && emailControl.value) {
        phoneControl.clearValidators();
        phoneControl.updateValueAndValidity();
      }
      if (phoneControl.value && phoneControl.value.length >= 10) {
        emailControl.clearValidators();
        emailControl.updateValueAndValidity();
        if (!emailControl.value) {
          const randomNum = Math.floor(Math.random() * 1000000000000);
          emailControl.patchValue(`${randomNum}@no-email.com`);
        }
      }
    }
  }

  private getAddressIds(formValue: any): string[] {
    if (formValue.whereToAdd === 'all') {
      return this.addresses.map(address => address.id);
    }
    return formValue.propertiesToAdd === '' ? [] : formValue.propertiesToAdd;
  }

  private getServiceTypeIds(formValue: any): string[] {
    return formValue.serviceCategories === '' ? [] : formValue.serviceCategories;
  }

  async getServiceCategoryItems() {
    const serviceCategories = await this.pros.getServiceCategories();
    const array: any = [];
    serviceCategories.map((service) => {
      array.push({
        viewValue: service.name,
        value: service.id,
      });
    });
    return array;
  }

  learnMoreAddPros() {
    const url = 'https://help.tidy.com/my-pros';
    this.util.openUrl(url);
  }

  getWhereToAddItems(addresses) {
    this.whereToAddItems = [
      {
        value: 'all',
        viewValue: 'All Properties',
      },
      {
        value: 'select',
        viewValue: 'Select Properties',
      },
    ];
    this.propertiesToAddItems = [];
    addresses.map((address) => {
      this.propertiesToAddItems.push({
        viewValue: address.address_name
          ? address.address_name
          : address.address1,
        value: address.id,
      });
    });
  }

  selectWhereToAdd(form) {
    if (form.value.whereToAdd === 'select') {
      form.controls.propertiesToAdd.setValidators([Validators.required]);
    } else {
      form.controls.propertiesToAdd.clearValidators();
    }
    form.controls.propertiesToAdd.updateValueAndValidity();
  }

  removePro(form: UntypedFormGroup) {
    this.forms.removeAt(this.forms.controls.indexOf(form));
  }

  async skipStep() {
    try {
      await this.onboardingProvider.markGoalAsCompleted('addPros');
      // Add Pros is an independent flow: it writes ONLY `pros=completed`. Notify Pros is a
      // separate step that owns the `notify` signal.
      await this.client.addMetadata({ key: `onboarding_step.${this.addressId}.pros`, value: 'completed' });
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  }

  // Step 2: Smart Numbers

  // Generates both proxy numbers up front so they are ready by the time the
  // user reaches the Smart Numbers step. Kicked off from ngOnInit on landing.
  private async generateSmartNumbers(): Promise<void> {
    this.loadingNumbers = true;
    this.cdr.markForCheck();
    try {
      const existingNumbers = await this.communication.getProxyNumbers();
      const existingPro = existingNumbers?.find(n => n.number_type === 'homekeeper');
      const existingGuest = existingNumbers?.find(n => n.number_type === 'guest');

      if (existingPro && existingGuest) {
        this.proxyProNumber = this.formatPhoneNumber(existingPro.phone_number);
        this.proxyGuestNumber = this.formatPhoneNumber(existingGuest.phone_number);
        this.loadingNumbers = false;
        this.cdr.markForCheck();
        return;
      }

      const subscriptionTypes = await this.me.getSubscriptionTypes();
      const hasNonTrialPaidSubscription = await this.me.checkIfHasNonTrialPaidSubscription(subscriptionTypes);
      if (!hasNonTrialPaidSubscription) {
        this.smartNumberSubscriptionMissing = true;
        this.loadingNumbers = false;
        this.cdr.markForCheck();
        return;
      }

      const promises: Promise<any>[] = [];
      if (!existingPro) {
        promises.push(
          this.communication.createProxyNumber('homekeeper').then(response => {
            this.proxyProNumber = this.formatPhoneNumber(response.phone_number);
            localStorage.setItem('cachedProNumber', this.proxyProNumber);
          })
        );
      } else {
        this.proxyProNumber = this.formatPhoneNumber(existingPro.phone_number);
      }

      if (!existingGuest) {
        promises.push(
          this.communication.createProxyNumber('guest').then(response => {
            this.proxyGuestNumber = this.formatPhoneNumber(response.phone_number);
            localStorage.setItem('cachedGuestNumber', this.proxyGuestNumber);
          })
        );
      } else {
        this.proxyGuestNumber = this.formatPhoneNumber(existingGuest.phone_number);
      }

      await Promise.all(promises);
    } catch (err) {
      const errorMessage = (err as any)?.error?.message || 'Failed to load smart numbers. Please try again.';
      this.util.showError(errorMessage);
    }
    this.loadingNumbers = false;
    this.cdr.markForCheck();
  }

  // Invoked when the user reaches the Smart Numbers step. Generation is already
  // running from ngOnInit; this just waits for it and skips the step when the
  // account has no paid subscription.
  private async loadSmartNumbers(): Promise<void> {
    await (this.smartNumbersPromise ?? (this.smartNumbersPromise = this.generateSmartNumbers()));
    if (this.smartNumberSubscriptionMissing) {
      this.goToNextStep();
    }
  }

  private formatPhoneNumber(number: string): string {
    if (!number) { return ''; }
    return number.replace(/^\+?\d{1,3}(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
  }

  copyToClipboard = (number: string) => {
    this.util.copyToClipBoard(number);
  };

  goToNextStep = () => {
    if (this.currentStep < 3) {
      this.currentStep = (this.currentStep + 1) as 1 | 2 | 3;
      this.updateBackAction();
      this.cdr.markForCheck();
      if (this.currentStep === 3) {
        this.loadNotifyProsData();
      }
    }
  };

  // Step 3: Notify Pros

  private async loadNotifyProsData(): Promise<void> {
    this.notifyLoaded = false;
    this.cdr.markForCheck();
    try {
      const [meData, allPros, addressPros, addresses] = await Promise.all([
        this.me.load(),
        this.pros.getAllPrivatePros(),
        this.pros.getAllPrivateProsByAddress(this.addressId),
        this.client.getAddresses()
      ]);
      this.userName = meData.current_user?.name || '[YOUR NAME]';
      this.businessName = meData.customer_account?.account_name || meData.customer_account?.name || '[YOUR BUSINESS]';
      const address = addresses.find(a => a.id === this.addressId);
      this.addressName = address?.address_name || address?.address1 || 'this address';
      this.allProItems = allPros.map(pro => ({
        value: pro.id,
        viewValue: `${pro.first_name} ${pro.last_name}`
      }));
      this.addressProItems = addressPros.map(pro => ({
        value: pro.id,
        viewValue: `${pro.first_name} ${pro.last_name}`
      }));
      this.selectedPros = [...this.allProItems];
      this.proScopeItems = [
        { value: 'address', viewValue: `All pros at ${this.addressName}` },
        { value: 'all', viewValue: 'All pros' },
        { value: 'select', viewValue: 'Select pros' }
      ];
      this.generateMessage();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
    this.notifyLoaded = true;
    this.cdr.markForCheck();
  }

  generateMessage = () => {
    const name = this.userName;
    const business = this.businessName;
    const message = `Hi, it's ${name} from ${business}.

\u{1F4F1} This is my new number - please use this for all communication.

I'm using TIDY to help me manage my property. They will chat with you on this line too.

Don't worry, it should mean very little changes for you:
\u2713 Pay stays the same
\u2713 No app required (they have one if you like)

Next steps:
\u{1F4C6} Keep doing any scheduled jobs.
\u{1F44D} Accept new job requests that come in.

I appreciate you and am excited to keep working together.`;
    this.generatedMessage = message;
    this.notifyForm.patchValue({ messageText: message });
  };

  updateSelectedPros = (pro: any) => {
    if (this.selectedPros.includes(pro)) {
      this.selectedPros = this.selectedPros.filter(p => p !== pro);
    } else {
      this.selectedPros.push(pro);
    }
  };

  copyMessage = () => {
    this.didCopyMessage = true;
    const messageToSend = this.notifyForm.get('messageText')?.value || this.generatedMessage;
    const textArea = document.createElement('textarea');
    textArea.value = messageToSend;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    this.messageCopied = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.messageCopied = false;
      this.cdr.markForCheck();
    }, 3000);
  };

  private getProsToSend(): any[] {
    const scope = this.notifyForm.value.proScope;
    if (scope === 'address') { return this.addressProItems; }
    if (scope === 'all') { return this.allProItems; }
    return this.selectedPros;
  }

  sendSMSToPros = async () => {
    try {
      const prosToSend = this.getProsToSend();
      if (prosToSend.length === 0) {
        return this.util.showError('No pros selected to send to.');
      }
      this.sendingInProgress = true;
      this.prosSendStatus = prosToSend.map(pro => ({
        pro,
        status: 'pending' as const
      }));
      this.cdr.markForCheck();
      for (let i = 0; i < this.prosSendStatus.length; i++) {
        const item = this.prosSendStatus[i];
        item.status = 'sending';
        this.cdr.markForCheck();
        try {
          const messageToSend = this.notifyForm.get('messageText')?.value || this.generatedMessage;
          const payload = {
            message: {
              text: messageToSend,
              files: []
            },
            to_homekeeper_id: item.pro.value
          };
          await this.communication.sendSharedInboxMessage(payload);
          item.status = 'success';
        } catch (error) {
          item.status = 'error';
          item.error = error?.error?.message || 'Failed to send message';
        }
        this.cdr.markForCheck();
        if (i < this.prosSendStatus.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      const successCount = this.prosSendStatus.filter(item => item.status === 'success').length;
      const errorCount = this.prosSendStatus.filter(item => item.status === 'error').length;
      if (successCount > 0 && errorCount === 0) {
        this.completeNotifyStep();
      } else if (successCount > 0 && errorCount > 0) {
        this.util.showWarning(`Message sent to ${successCount} pros. Failed for ${errorCount} pros.`);
      } else {
        this.util.showError('Failed to send messages to pros');
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    } finally {
      this.sendingInProgress = false;
      this.cdr.markForCheck();
    }
  };

  completeNotifyStep = async () => {
    if (!this.didCopyMessage && this.notifyForm.value.howToSend === 'copy') {
      return this.util.showError('Please copy the message first');
    }
    try {
      // The Notify step owns ONLY the `notify` signal — `pros` was already written when the
      // user advanced past step 1. Co-writing `pros` here re-couples the two flows.
      await this.client.addMetadata({ key: `onboarding_step.${this.addressId}.notify`, value: 'completed' });
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };

  skipNotifyStep = async () => {
    try {
      // Notify step owns ONLY the `notify` signal (see completeNotifyStep).
      await this.client.addMetadata({ key: `onboarding_step.${this.addressId}.notify`, value: 'completed' });
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };

}
